import { useQueryClient } from '@tanstack/react-query'
import debounce from 'lodash.debounce'
import { useRouter } from 'next/router'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { MESSAGE_TEMPLATES_QUERY_KEY } from '../components/MessageTemplates'
import {
  buildMessageTemplateDefinition,
  resolveMessageTemplateType,
} from '../constants/message-template'
import { MessageTemplateCtx } from '../providers/MessageTemplate.provider'
import type {
  IMessageTemplate,
  MessageTemplateStatus,
  MessageTemplateStatusUpdateInput,
  MessageTemplateUpdateInput,
} from '../providers/MessageTemplate.provider'
import { messageTemplateStatusBySwitchChecked } from '../providers/MessageTemplate.provider'
import { IMessageTemplateSchema } from '../schemas/message-template.schema'
import type { ErrorToastHandler } from './useError'
import { useError } from './useError'

const resolveMessageTemplateStatusFromChecked = (
  checked: boolean,
): MessageTemplateStatus => {
  if (checked) return messageTemplateStatusBySwitchChecked.true
  return messageTemplateStatusBySwitchChecked.false
}

const updateMessageTemplateStatus = (
  messageTemplates: IMessageTemplate[] | undefined,
  messageTemplateId: string,
  status: MessageTemplateStatus,
) => {
  if (!messageTemplates) return messageTemplates

  return messageTemplates.map((messageTemplate) => {
    if (messageTemplate._id !== messageTemplateId) return messageTemplate

    return {
      ...messageTemplate,
      status,
    }
  })
}

export const useMessageTemplateEdit = (props: {
  messageTemplate: IMessageTemplate
  onErrorToast?: ErrorToastHandler
  onUpdateMessageTemplateStatusSuccess?: () => void
}) => {
  const router = useRouter()
  const routerMessageTemplateType = useMemo(
    () => resolveMessageTemplateType(router.query?.flow),
    [router.query?.flow],
  )
  const messageTemplateType = useMemo(() => {
    if (router.query?.flow) return routerMessageTemplateType

    return props.messageTemplate.type ?? routerMessageTemplateType
  }, [
    props.messageTemplate.type,
    router.query?.flow,
    routerMessageTemplateType,
  ])
  const formId = 'update-message-template'

  const error = useError(props.onErrorToast)

  const queryClient = useQueryClient()

  const [isLoading, setIsLoading] = useState(false)
  const [isStatusLoading, setIsStatusLoading] = useState(false)
  const lastRequestedStatusRef = useRef<MessageTemplateStatus>()

  const { updateMethod, updateStatusMethod } = useContext(MessageTemplateCtx)!

  const onUpdateMessageTemplate = useCallback(
    (data: IMessageTemplateSchema, onSuccess: () => void) => {
      setIsLoading(true)

      const update = {
        template: buildMessageTemplateDefinition(data),
        type: messageTemplateType,
      } satisfies MessageTemplateUpdateInput

      updateMethod(props.messageTemplate._id, update)
        .then((res) => {
          queryClient.invalidateQueries({
            queryKey: [MESSAGE_TEMPLATES_QUERY_KEY],
          })

          onSuccess()
        })
        .catch((err) => {
          console.error('Error updating message template', err)
          error.handleError(err)
        })
        .finally(() => setIsLoading(false))
    },
    [
      error.handleError,
      messageTemplateType,
      props.messageTemplate._id,
      queryClient,
      updateMethod,
    ],
  )

  const onUpdateMessageTemplateStatus = useCallback(
    (check: boolean) => {
      const status = resolveMessageTemplateStatusFromChecked(check)
      if (props.messageTemplate.status === status) return
      if (lastRequestedStatusRef.current === status) return

      const data = {
        status,
      } satisfies MessageTemplateStatusUpdateInput

      lastRequestedStatusRef.current = status
      setIsStatusLoading(true)

      updateStatusMethod(props.messageTemplate._id, data)
        .then(() => {
          queryClient.setQueriesData<IMessageTemplate[]>(
            {
              queryKey: [MESSAGE_TEMPLATES_QUERY_KEY],
            },
            (messageTemplates) =>
              updateMessageTemplateStatus(
                messageTemplates,
                props.messageTemplate._id,
                status,
              ),
          )

          props.onUpdateMessageTemplateStatusSuccess?.()
        })
        .catch((err) => {
          lastRequestedStatusRef.current = undefined
          console.error('Error updating message template status', err)
          error.handleError(err)
        })
        .finally(() => setIsStatusLoading(false))
    },
    [
      error.handleError,
      props.messageTemplate._id,
      props.messageTemplate.status,
      props.onUpdateMessageTemplateStatusSuccess,
      queryClient,
      updateStatusMethod,
    ],
  )

  const onUpdateMessageTemplateStatusRef = useRef(onUpdateMessageTemplateStatus)
  onUpdateMessageTemplateStatusRef.current = onUpdateMessageTemplateStatus

  const onDebouncedStatusChange = useCallback((checked: boolean) => {
    onUpdateMessageTemplateStatusRef.current(checked)
  }, [])

  const debounceSwitch = useMemo(
    () => debounce(onDebouncedStatusChange, 500),
    [onDebouncedStatusChange],
  )

  useEffect(() => () => debounceSwitch.cancel(), [debounceSwitch])

  return {
    onUpdateMessageTemplateStatus,
    debounceSwitch,
    isLoading,
    isStatusLoading,
    formId,
    onUpdateMessageTemplate,
  }
}
